import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useTheme } from '../../../contexts/ThemeContext';

const ServiceCard = ({ service, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isDarkMode } = useTheme();

  const handleScheduleNow = () => {
    window.location.href = '/pickup-scheduling';
  };

  const handleLearnMore = () => {
    // Smooth scroll to pricing section
    const pricingSection = document.getElementById('pricing-section');
    if (pricingSection) {
      pricingSection?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="group relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={`relative rounded-2xl p-6 border transition-all duration-500 ${isHovered ? 'shadow-elevation-4 transform -translate-y-3 scale-105' : 'shadow-elevation-1'
        } ${isDarkMode ?
          // Dark Theme Styles
          (isHovered ?
            (service?.category === 'wash' ? 'bg-gradient-to-br from-blue-900/30 to-blue-800/30 border-blue-600' :
              service?.category === 'dry-clean' ? 'bg-gradient-to-br from-green-900/30 to-green-800/30 border-green-600' :
                service?.category === 'premium' ? 'bg-gradient-to-br from-purple-900/30 to-purple-800/30 border-purple-600' :
                  service?.category === 'specialty' ? 'bg-gradient-to-br from-orange-900/30 to-orange-800/30 border-orange-600' :
                    'bg-gradient-to-br from-gray-800/30 to-gray-700/30 border-gray-600') :
            (service?.category === 'wash' ? 'bg-gradient-to-br from-blue-900/20 to-blue-800/20 border-blue-700' :
              service?.category === 'dry-clean' ? 'bg-gradient-to-br from-green-900/20 to-green-800/20 border-green-700' :
                service?.category === 'premium' ? 'bg-gradient-to-br from-purple-900/20 to-purple-800/20 border-purple-700' :
                  service?.category === 'specialty' ? 'bg-gradient-to-br from-orange-900/20 to-orange-800/20 border-orange-700' :
                    'bg-gradient-to-br from-gray-800/20 to-gray-700/20 border-gray-700')) :
          // Light Theme Styles
          (isHovered ?
            (service?.category === 'wash' ? 'bg-gradient-to-br from-blue-100 to-blue-200 border-blue-300' :
              service?.category === 'dry-clean' ? 'bg-gradient-to-br from-green-100 to-green-200 border-green-300' :
                service?.category === 'premium' ? 'bg-gradient-to-br from-purple-100 to-purple-200 border-purple-300' :
                  service?.category === 'specialty' ? 'bg-gradient-to-br from-orange-100 to-orange-200 border-orange-300' :
                    'bg-gradient-to-br from-gray-100 to-gray-200 border-gray-300') :
            (service?.category === 'wash' ? 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200' :
              service?.category === 'dry-clean' ? 'bg-gradient-to-br from-green-50 to-green-100 border-green-200' :
                service?.category === 'premium' ? 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200' :
                  service?.category === 'specialty' ? 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200' :
                    'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'))
        }`}>
        {/* Service Icon */}
        <div className="relative mb-6">
          <div className={`w-16 h-16 rounded-xl flex items-center justify-center transition-all duration-500 ${isHovered ?
              (service?.category === 'wash' ? 'bg-blue-600 scale-110' :
                service?.category === 'dry-clean' ? 'bg-green-600 scale-110' :
                  service?.category === 'premium' ? 'bg-purple-600 scale-110' :
                    service?.category === 'specialty' ? 'bg-orange-600 scale-110' :
                      service?.hoverBg) :
              (service?.category === 'wash' ? 'bg-blue-500' :
                service?.category === 'dry-clean' ? 'bg-green-500' :
                  service?.category === 'premium' ? 'bg-purple-500' :
                    service?.category === 'specialty' ? 'bg-orange-500' :
                      service?.iconBg)
            }`}>
            <Icon
              name={service?.icon}
              size={28}
              color={isHovered ? 'white' : 'white'}
            />
          </div>
          {service?.isPopular && (
            <div className="absolute -top-2 -right-2 bg-accent text-accent-foreground text-xs font-semibold px-2 py-1 rounded-full">
              Popular
            </div>
          )}
        </div>

        {/* Service Content */}
        <div className="space-y-4">
          <div>
            <h3 className={`text-xl font-semibold mb-2 transition-all duration-300 ${isDarkMode ?
                (isHovered ?
                  (service?.category === 'wash' ? 'text-blue-300' :
                    service?.category === 'dry-clean' ? 'text-green-300' :
                      service?.category === 'premium' ? 'text-purple-300' :
                        service?.category === 'specialty' ? 'text-orange-300' :
                          'text-primary') : 'text-primary-foreground') :
                (isHovered ?
                  (service?.category === 'wash' ? 'text-blue-700' :
                    service?.category === 'dry-clean' ? 'text-green-700' :
                      service?.category === 'premium' ? 'text-purple-700' :
                        service?.category === 'specialty' ? 'text-orange-700' :
                          'text-primary') : 'text-foreground')
              }`}>
              {service?.title}
            </h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {service?.description}
            </p>
          </div>

          {/* Features List */}
          <div className="space-y-2">
            {service?.features?.map((feature, idx) => (
              <div key={idx} className="flex items-center space-x-2">
                <Icon name="Check" size={16} color="var(--color-success)" />
                <span className={`text-sm ${isDarkMode ? 'text-primary-foreground' : 'text-foreground'}`}>{feature}</span>
              </div>
            ))}
          </div>

          {/* Pricing */}
          <div className={`flex items-center justify-between pt-4 border-t transition-all duration-300 ${isHovered ?
              (service?.category === 'wash' ? 'border-blue-300' :
                service?.category === 'dry-clean' ? 'border-green-300' :
                  service?.category === 'premium' ? 'border-purple-300' :
                    service?.category === 'specialty' ? 'border-orange-300' :
                      'border-primary') : 'border-border'
            }`}>
            <div>
              <span className="text-sm text-muted-foreground mr-1">Starts from</span>
              <span className={`text-2xl font-bold transition-all duration-300 ${isDarkMode ?
                  (isHovered ?
                    (service?.category === 'wash' ? 'text-blue-400' :
                      service?.category === 'dry-clean' ? 'text-green-400' :
                        service?.category === 'premium' ? 'text-purple-400' :
                          service?.category === 'specialty' ? 'text-orange-400' :
                            'text-primary') : 'text-primary') :
                  (isHovered ?
                    (service?.category === 'wash' ? 'text-blue-600' :
                      service?.category === 'dry-clean' ? 'text-green-600' :
                        service?.category === 'premium' ? 'text-purple-600' :
                          service?.category === 'specialty' ? 'text-orange-600' :
                            'text-primary') : 'text-primary')
                }`}>₹ {service?.price}</span>
              <span className="text-sm text-muted-foreground ml-1">{service?.unit}</span>
            </div>
            <div className="text-right">
              <div className={`text-sm font-medium ${isDarkMode ? 'text-primary-foreground' : 'text-foreground'}`}>{service?.duration}</div>
              <div className="text-xs text-muted-foreground">{service?.turnaround}</div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleLearnMore}
              className="flex-1 transition-all duration-300 hover:border-primary hover:text-primary"
            >
              Learn More
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleScheduleNow}
              iconName="Calendar"
              iconPosition="right"
              className="flex-1 transition-all duration-300 hover:shadow-elevation-2"
            >
              Schedule Now
            </Button>
          </div>
        </div>

        {/* Hover Overlay Effect - Removed to prevent text fading */}
      </div>
    </motion.div>
  );
};

export default ServiceCard;